import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, IconButton, LinearProgress, Portal, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ToastContext from '../../context/ToastContext';
import { setToastRef } from '../../util/toast';
import { SWSnackbar } from '../mui/Snackbar';
import { FormattedMessage } from '../../util/reactIntl';
import NamedLink from '../NamedLink';

export const ToastProvider = props => {
  const { children } = props;
  const [state, setState] = useState({
    open: false,
    message: '',
    messageKey: null,
    messageValues: null,
    action: null,
    link: null,
    autoHideDuration: 8000,
    showCountdown: false,
    countdownSeconds: null,
    onAutoHide: null,
  });
  const [countdownProgress, setCountdownProgress] = useState(100);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const actionRef = useRef(null);
  const onAutoHideRef = useRef(null);
  const hideConsumedRef = useRef(false);
  const countdownRef = useRef({
    startTime: 0,
    accumulatedPause: 0,
    pauseStartTime: null,
  });
  actionRef.current = state.action;
  onAutoHideRef.current = state.onAutoHide;

  const showToast = useCallback((message, options = {}) => {
    const {
      action,
      link,
      autoHideDuration = 8000,
      showCountdown = false,
      countdownSeconds = null,
      messageValues = null,
      onAutoHide = null,
    } = options;
    const messageKey = message?.messageKey || null;
    const messageStr = typeof message === 'string' ? message : '';
    const values = message?.values || messageValues;
    const actionWithKey = action
      ? {
          ...action,
          labelKey: action.labelKey || null,
        }
      : null;
    const linkConfig = link?.routeName && link?.labelKey ? link : null;
    const seconds = typeof countdownSeconds === 'number' ? countdownSeconds : null;
    setState({
      open: true,
      message: messageStr,
      messageKey,
      messageValues: values,
      action: actionWithKey,
      link: linkConfig,
      autoHideDuration: seconds ? seconds * 1000 : autoHideDuration,
      showCountdown: showCountdown || !!seconds,
      countdownSeconds: seconds,
      onAutoHide,
    });
    setCountdownProgress(100);
    setSecondsLeft(seconds);
    setIsHovered(false);
    hideConsumedRef.current = false;
    countdownRef.current = {
      startTime: Date.now(),
      accumulatedPause: 0,
      pauseStartTime: null,
    };
  }, []);

  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setState(prev => ({ ...prev, open: false }));
  }, []);

  const handleManualClose = useCallback(() => {
    setState(prev => ({ ...prev, open: false }));
  }, []);

  const handleActionClick = useCallback(() => {
    hideConsumedRef.current = true;
    actionRef.current?.onClick?.();
    setState(prev => ({ ...prev, open: false }));
  }, []);

  const contextValue = { showToast };

  useEffect(() => {
    setToastRef(showToast);
    return () => setToastRef(null);
  }, [showToast]);

  useEffect(() => {
    if (!state.open || !state.showCountdown) return;
    const dur = state.autoHideDuration;
    const id = setInterval(() => {
      const ref = countdownRef.current;
      const now = Date.now();
      if (isHovered) {
        if (!ref.pauseStartTime) ref.pauseStartTime = now;
        return;
      }
      if (ref.pauseStartTime) {
        ref.accumulatedPause += now - ref.pauseStartTime;
        ref.pauseStartTime = null;
      }
      const elapsed = now - ref.startTime - ref.accumulatedPause;
      const progress = Math.max(0, 100 - (elapsed / dur) * 100);
      setCountdownProgress(progress);
      if (state.countdownSeconds) {
        setSecondsLeft(Math.max(0, Math.ceil((dur - elapsed) / 1000)));
      }
      if (elapsed >= dur) {
        if (!hideConsumedRef.current) {
          hideConsumedRef.current = true;
          onAutoHideRef.current?.();
        }
        setState(prev => ({ ...prev, open: false }));
      }
    }, 50);
    return () => clearInterval(id);
  }, [state.open, state.showCountdown, state.autoHideDuration, state.countdownSeconds, isHovered]);

  const displayMessage = state.messageKey ? (
    <FormattedMessage
      id={state.messageKey}
      values={{
        ...(state.messageValues || {}),
        ...(secondsLeft != null ? { seconds: secondsLeft } : {}),
      }}
    />
  ) : (
    state.message
  );

  const messageContent = (
    <Stack sx={{ width: '100%', minWidth: 0, flex: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
        sx={{ flexWrap: 'nowrap', width: '100%', minWidth: 360 }}
      >
        <span>{displayMessage}</span>
        {state.link ? (
          <NamedLink
            name={state.link.routeName}
            params={state.link.params || {}}
            className={null}
            style={{ color: 'inherit', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            <FormattedMessage id={state.link.labelKey} />
          </NamedLink>
        ) : null}
      </Stack>
      {state.showCountdown && state.open && (
        <LinearProgress
          variant="determinate"
          value={countdownProgress}
          sx={{
            mt: 1,
            height: 4,
            borderRadius: 1,
            backgroundColor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': { backgroundColor: 'white' },
          }}
        />
      )}
    </Stack>
  );

  const actionContent = (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
      {state.action ? (
        <Button
          color="inherit"
          size="small"
          onClick={handleActionClick}
          sx={{ minWidth: 0, py: 0.5 }}
        >
          {state.action?.labelKey ? (
            <FormattedMessage id={state.action.labelKey} />
          ) : (
            state.action?.label
          )}
        </Button>
      ) : null}
      <IconButton
        size="small"
        color="inherit"
        onClick={handleManualClose}
        aria-label="Close"
        sx={{ p: 0.5 }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Stack>
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <Portal container={typeof document !== 'undefined' ? document.body : null}>
        <SWSnackbar
          open={state.open}
          autoHideDuration={state.showCountdown ? null : state.autoHideDuration}
          onClose={handleClose}
          message={messageContent}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ zIndex: 99999 }}
          action={actionContent}
          slotProps={{
            content: {
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
            },
          }}
        />
      </Portal>
    </ToastContext.Provider>
  );
};
